import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RefreshCw,
  LogOut,
  KeyRound,
  Mail,
  Phone,
  Linkedin,
  Github,
  Building2,
  Filter,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { getClientDashboard, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CLIENT } from "@/constants/testIds";
import { scoreJob, matchBadge } from "@/lib/jobMatch";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import Seo from "@/components/Seo";

const EASE = [0.76, 0, 0.24, 1];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
};

export default function ClientDashboardPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ client: null, jobs: [], applications: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [jobFilter, setJobFilter] = useState("all"); // "all" or a job id

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await getClientDashboard();
      setData(d);
    } catch (e) {
      if (e?.response?.status === 401) {
        signOut();
        navigate("/client/login", { replace: true });
        return;
      }
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [navigate, signOut]);

  useEffect(() => {
    load();
  }, [load]);

  const onLogout = () => {
    signOut();
    toast.success("Signed out.");
    navigate("/client/login", { replace: true });
  };

  const downloadCv = async (a) => {
    try {
      const resp = await api.get(`/candidates/${a.id}/cv`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.cv_filename || `cv-${(a.name || "candidate").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "CV not stored on file." : "Could not download CV.");
    }
  };

  const jobsById = useMemo(() => {
    const m = {};
    for (const j of data.jobs || []) m[j.id] = j;
    return m;
  }, [data.jobs]);

  const scoredApps = useMemo(() => {
    const list = (data.applications || []).map((a) => {
      const job = a.job_id ? jobsById[a.job_id] : null;
      const m = job ? scoreJob(a.skills || [], job.tech_stack || []) : null;
      return { ...a, _job: job, _match: m };
    });
    // sort by score (nulls last), then by created_at desc
    list.sort((a, b) => {
      const sa = a._match?.score ?? -1;
      const sb = b._match?.score ?? -1;
      if (sa !== sb) return sb - sa;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return list;
  }, [data.applications, jobsById]);

  const filteredApps = useMemo(() => {
    if (jobFilter === "all") return scoredApps;
    if (jobFilter === "company") return scoredApps.filter((a) => !a.job_id);
    return scoredApps.filter((a) => a.job_id === jobFilter);
  }, [scoredApps, jobFilter]);

  const client = data.client || user;

  return (
    <div className="pt-32 pb-24 bg-[var(--bg)] min-h-screen" data-testid={CLIENT.dashboard}>
      <Seo title="Hiring partner portal" description="Live shortlist and match scores for your open roles." path="/client" noIndex />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10">
          <div>
            <p className="overline mb-4">
              Hiring partner{client?.company ? ` · ${client.company}` : ""}
            </p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[var(--ink)]"
            >
              {client?.name ? `Welcome, ${client.name.split(" ")[0]}.` : "Your inbox."}
            </motion.h1>
            <p className="mt-4 text-[var(--ink-80)]">
              {(data.jobs || []).length} open role{(data.jobs || []).length === 1 ? "" : "s"} ·{" "}
              {(data.applications || []).length} candidate{(data.applications || []).length === 1 ? "" : "s"} in pipeline
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={load} disabled={loading} className="btn-ghost">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>
            <button onClick={() => setPwOpen(true)} className="btn-ghost">
              <KeyRound className="w-4 h-4" />
              <span>Change password</span>
            </button>
            <button
              onClick={onLogout}
              className="btn-ghost"
              data-testid={CLIENT.logout}
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="border border-red-500/40 bg-red-500/5 text-red-700 p-4 mb-6">
            {error}
          </div>
        )}

        {/* Assigned roles */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)]">
              Your open roles
            </h2>
          </div>
          {(data.jobs || []).length === 0 ? (
            <div className="border border-[var(--line-strong)] bg-[var(--bg-2)] py-12 text-center">
              <p className="font-serif-accent italic text-[var(--accent)] text-xl mb-2">
                No roles assigned yet.
              </p>
              <p className="text-[var(--ink-80)] text-sm">
                Your engagement manager will link roles to your account here.
                {client?.company && (
                  <>
                    {" "}Applications from candidates who list <strong>{client.company}</strong> as their current company will still appear below.
                  </>
                )}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data.jobs || []).map((j) => {
                const count = scoredApps.filter((a) => a.job_id === j.id).length;
                const active = jobFilter === j.id;
                return (
                  <button
                    key={j.id}
                    onClick={() => setJobFilter(active ? "all" : j.id)}
                    data-testid={CLIENT.jobCard(j.id)}
                    className={`text-left border p-5 transition-all group ${
                      active
                        ? "border-[var(--accent)] bg-[var(--bg-2)]"
                        : "border-[var(--line-strong)] hover:bg-[var(--bg-2)]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="font-display text-lg text-[var(--ink)] leading-snug">
                        {j.title}
                      </p>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-0.5 whitespace-nowrap">
                        {count} in pipeline
                      </span>
                    </div>
                    <p className="text-xs text-[var(--ink-70)] mb-3">
                      {j.company} · {j.location} · {j.remote_type}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(j.tech_stack || []).slice(0, 6).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-70)] border border-[var(--line-strong)] px-1.5 py-0.5"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Pipeline */}
        <section>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)]">
              Candidate pipeline
            </h2>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--ink-70)]">
              <Filter className="w-3.5 h-3.5" />
              <span>
                {jobFilter === "all"
                  ? "All roles"
                  : jobFilter === "company"
                  ? "Company match only"
                  : jobsById[jobFilter]?.title || "Filtered"}
              </span>
              {jobFilter !== "all" && (
                <button
                  onClick={() => setJobFilter("all")}
                  className="link-slide ml-2 text-[var(--accent)]"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {!loading && filteredApps.length === 0 && (
            <div
              data-testid={CLIENT.empty}
              className="border border-[var(--line-strong)] bg-[var(--bg-2)] py-16 text-center"
            >
              <p className="font-serif-accent italic text-[var(--accent)] text-xl mb-2">
                No candidates yet.
              </p>
              <p className="text-[var(--ink-80)] text-sm">
                Applications will appear here as candidates apply to your assigned roles.
              </p>
            </div>
          )}

          {filteredApps.length > 0 && (
            <div className="border border-[var(--line-strong)]">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[var(--line-strong)] text-[10px] uppercase tracking-[0.24em] text-[var(--ink-50)] bg-[var(--bg-2)]">
                <div className="col-span-3">Candidate</div>
                <div className="col-span-3">For role</div>
                <div className="col-span-2">Match</div>
                <div className="col-span-3">Skills</div>
                <div className="col-span-1 text-right">Applied</div>
              </div>
              {filteredApps.map((a, i) => {
                const badge = a._match ? matchBadge(a._match.score) : null;
                return (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.02 * i }}
                    data-testid={CLIENT.appRow(a.id)}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-6 border-b border-[var(--line)] hover:bg-[var(--bg-2)] transition-colors"
                  >
                    <div className="col-span-3">
                      <p className="text-[var(--ink)] font-medium">{a.name}</p>
                      <a
                        href={`mailto:${a.email}`}
                        className="link-slide text-[var(--ink-80)] text-sm inline-flex items-center gap-1.5 break-all"
                      >
                        <Mail className="w-3 h-3" /> {a.email}
                      </a>
                      {a.phone && (
                        <p className="text-[var(--ink-50)] text-xs mt-1 inline-flex items-center gap-1.5">
                          <Phone className="w-3 h-3" /> {a.phone}
                        </p>
                      )}
                      <div className="flex gap-2 mt-2 items-center">
                        {a.linkedin_url && (
                          <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[var(--ink-70)] hover:text-[var(--accent)]">
                            <Linkedin className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {a.github_url && (
                          <a href={a.github_url} target="_blank" rel="noopener noreferrer" className="text-[var(--ink-70)] hover:text-[var(--accent)]">
                            <Github className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {a.cv_storage_path && (
                          <button
                            onClick={() => downloadCv(a)}
                            title="Download CV"
                            data-testid={`client-cv-download-${a.id}`}
                            className="text-[var(--ink-70)] hover:text-[var(--accent)] inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] border-l border-[var(--line-strong)] pl-2 ml-1"
                          >
                            <FileDown className="w-3.5 h-3.5" />
                            <span>CV</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-span-3">
                      {a._job ? (
                        <Link
                          to={`/jobs/${a._job.id}`}
                          className="link-slide text-[var(--ink)] text-sm"
                        >
                          {a._job.title}
                        </Link>
                      ) : (
                        <span className="text-[var(--ink-70)] text-sm inline-flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5" />
                          {a.current_company || "Company match"}
                        </span>
                      )}
                      {a.headline && (
                        <p className="text-[var(--ink-50)] text-xs mt-1 leading-snug">{a.headline}</p>
                      )}
                      {typeof a.years_of_experience === "number" && (
                        <p className="text-[var(--ink-50)] text-[11px] uppercase tracking-[0.2em] mt-1">
                          {a.years_of_experience} yrs
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      {a._match ? (
                        <div>
                          <p className="font-display text-3xl tracking-tight text-[var(--ink)] leading-none">
                            {a._match.score}
                            <span className="text-sm text-[var(--ink-50)]"> %</span>
                          </p>
                          <p className="text-[10px] uppercase tracking-[0.22em] mt-1"
                            style={{ color: badge?.tone === "strong" ? "var(--accent)" : "var(--ink-70)" }}
                          >
                            {badge?.label}
                          </p>
                        </div>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)]">
                          Company match
                        </span>
                      )}
                    </div>
                    <div className="col-span-3">
                      <div className="flex flex-wrap gap-1">
                        {(a.skills || []).slice(0, 6).map((s) => {
                          const hit = a._match?.matched?.some(
                            (m) => m.toLowerCase() === s.toLowerCase()
                          );
                          return (
                            <span
                              key={s}
                              className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 border ${
                                hit
                                  ? "border-[var(--accent)] text-[var(--accent)]"
                                  : "border-[var(--line-strong)] text-[var(--ink-70)]"
                              }`}
                            >
                              {s}
                            </span>
                          );
                        })}
                        {(a.skills || []).length > 6 && (
                          <span className="text-[10px] text-[var(--ink-50)]">
                            +{a.skills.length - 6}
                          </span>
                        )}
                      </div>
                      {a.summary && (
                        <p className="text-[var(--ink-70)] text-xs mt-2 line-clamp-2">
                          {a.summary}
                        </p>
                      )}
                    </div>
                    <div className="col-span-1 md:text-right">
                      <p className="text-[var(--ink-50)] text-[10px] uppercase tracking-[0.2em]">
                        {formatDate(a.created_at)}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
